import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, MenuItem, Button, Chip, IconButton, InputAdornment, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, Grid,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, Inventory as AssetIcon } from '@mui/icons-material';
import { accountingAPI } from '../../services/api';
import { money, errorText } from '../../utils/format';
import { fmtDate, localDate } from '../../utils/dates';
import { StatCard } from './common';
import BalanceIcon from '@mui/icons-material/AccountBalanceWallet';
import BuildIcon from '@mui/icons-material/Build';

const CATEGORIES = ['SKILL_LAB', 'FURNITURE', 'LAB_EQUIPMENT', 'IT', 'VEHICLE', 'LIBRARY', 'OTHER'];
const CONDITIONS = ['GOOD', 'NEEDS_REPAIR', 'UNDER_REPAIR', 'DISPOSED'];
const conditionColor = { GOOD: 'success', NEEDS_REPAIR: 'warning', UNDER_REPAIR: 'warning', DISPOSED: 'default' };
const emptyForm = () => ({ name: '', category: 'IT', purchaseDate: localDate(), cost: '', vendorId: '', location: '', serialNo: '', condition: 'GOOD', notes: '' });

export default function AssetsTab({ notify }) {
  const [all, setAll] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('ALL');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, v, s] = await Promise.all([accountingAPI.getAssets(), accountingAPI.getVendors(), accountingAPI.getAssetsSummary()]);
      setAll(a.data); setVendors(v.data); setSummary(s.data);
    } catch (e) { notify(errorText(e, 'Could not load assets'), 'error'); }
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const rows = all.filter((a) => cat === 'ALL' || a.category === cat)
    .filter((a) => !q || `${a.name} ${a.location || ''} ${a.serialNo || ''}`.toLowerCase().includes(q.toLowerCase()));

  const openDialog = (a = null) => {
    setEditing(a);
    setForm(a ? { ...emptyForm(), ...a, vendorId: a.vendorId || '' } : emptyForm());
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { notify('Please enter the asset name', 'error'); return; }
    try {
      const data = { ...form, cost: form.cost === '' ? null : Number(form.cost), vendorId: form.vendorId || null };
      if (editing) await accountingAPI.updateAsset(editing.id, data); else await accountingAPI.createAsset(data);
      notify(editing ? 'Asset updated' : 'Asset added');
      setOpen(false);
      load();
    } catch (e) { notify(errorText(e, 'Could not save the asset'), 'error'); }
  };

  const remove = async () => {
    try { await accountingAPI.deleteAsset(confirm.id); notify('Asset deleted'); setConfirm(null); load(); }
    catch (e) { notify(errorText(e, 'Could not delete'), 'error'); }
  };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <StatCard title="Total assets" value={summary?.totalAssets ?? 0} icon={AssetIcon} gradient="linear-gradient(135deg,#6a1b9a,#ab47bc)" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard title="Total value" value={money(summary?.totalValue)} icon={BalanceIcon} gradient="linear-gradient(135deg,#0288d1,#26c6da)" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard title="Needs attention" value={summary?.needsAttention ?? 0} icon={BuildIcon} gradient="linear-gradient(135deg,#ef6c00,#ffa726)" subtitle="Needs / under repair" />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Search assets..." value={q} onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <TextField select size="small" label="Category" value={cat} onChange={(e) => setCat(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="ALL">All categories</MenuItem>
            {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c.replace('_', ' ')}</MenuItem>)}
          </TextField>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>Add asset</Button>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Name</TableCell><TableCell>Category</TableCell><TableCell>Location</TableCell><TableCell>Purchased</TableCell>
              <TableCell align="right">Cost</TableCell><TableCell>Condition</TableCell><TableCell align="center">Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell>{a.name}{a.serialNo ? <Typography variant="caption" display="block" color="text.secondary">SN: {a.serialNo}</Typography> : null}</TableCell>
                  <TableCell><Chip size="small" label={(a.category || 'OTHER').replace('_', ' ')} /></TableCell>
                  <TableCell>{a.location || '-'}</TableCell>
                  <TableCell>{a.purchaseDate ? fmtDate(a.purchaseDate) : '-'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{a.cost ? money(a.cost) : '-'}</TableCell>
                  <TableCell><Chip size="small" label={a.condition} color={conditionColor[a.condition] || 'default'} /></TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => openDialog(a)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => setConfirm(a)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>No assets recorded yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#004d40', color: 'white' }}>{editing ? 'Edit asset' : 'Add asset'}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}><TextField fullWidth size="small" label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c.replace('_', ' ')}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Condition" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}>
                {CONDITIONS.map((c) => <MenuItem key={c} value={c}>{c.replace('_', ' ')}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="date" label="Purchase date" InputLabelProps={{ shrink: true }} value={form.purchaseDate || ''} onChange={(e) => setForm({ ...form, purchaseDate: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="number" label="Cost (₹)" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Vendor" value={form.vendorId} onChange={(e) => setForm({ ...form, vendorId: e.target.value })}>
                <MenuItem value="">None</MenuItem>
                {vendors.map((v) => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Serial no" value={form.serialNo} onChange={(e) => setForm({ ...form, serialNo: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" multiline rows={2} label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>{editing ? 'Update' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete asset?</DialogTitle>
        <DialogContent><Typography>{confirm?.name} will be removed from the register. This cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setConfirm(null)}>Cancel</Button><Button color="error" variant="contained" onClick={remove}>Delete</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
